// Square initial tile shown for each company in the index list
// and at the top of the show page. Color is computed server-side
// via `V2::AvatarColor.for(name)` so it stays stable across
// sessions and matches what the row in the inbox would show.

interface AvatarTileProps {
  initial: string
  color: string
  size?: 32 | 36 | 56 | 64
  rounded?: "md" | "lg" | "xl"
}

const SIZE_PX: Record<NonNullable<AvatarTileProps["size"]>, number> = {
  32: 32,
  36: 36,
  56: 56,
  64: 64,
}

const TEXT_PX: Record<NonNullable<AvatarTileProps["size"]>, number> = {
  32: 13,
  36: 16,
  56: 22,
  64: 26,
}

const RADIUS_PX: Record<NonNullable<AvatarTileProps["rounded"]>, number> = {
  md: 8,
  lg: 10,
  xl: 14,
}

export function AvatarTile({
  initial,
  color,
  size = 32,
  rounded = "md",
}: AvatarTileProps) {
  const px = SIZE_PX[size]
  const fontPx = TEXT_PX[size]
  const radius = RADIUS_PX[rounded]

  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center shrink-0 font-semibold text-[#F0EDE6]"
      style={{
        width: px,
        height: px,
        borderRadius: radius,
        backgroundColor: color,
        fontSize: fontPx,
        lineHeight: `${Math.round(fontPx * 1.2)}px`,
      }}
    >
      {initial}
    </div>
  )
}

interface CircleAvatarProps {
  initials: string
  color: string
  size?: 20 | 22 | 26 | 32
}

const CIRCLE_TEXT_PX: Record<NonNullable<CircleAvatarProps["size"]>, number> = {
  20: 9,
  22: 9,
  26: 10,
  32: 11,
}

export function CircleAvatar({
  initials,
  color,
  size = 32,
}: CircleAvatarProps) {
  const fontPx = CIRCLE_TEXT_PX[size]

  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center shrink-0 rounded-full font-semibold text-[#F0EDE6]"
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        fontSize: fontPx,
        lineHeight: `${Math.round(fontPx * 1.3)}px`,
      }}
    >
      {initials}
    </div>
  )
}
